import { type SurfaceSdkContexts } from '../../types';

import {
	type Action,
	type ActionBus,
	type ActionDeclaration,
	type Config,
	type Contexts,
	type CreateActionBusAtModule,
	type Dispatch,
	type ExpandAction,
	type Middleware,
	type SideEffect,
	type Subscriber,
} from './types';
import { extractContextsFromAction } from './utils';

export const ContextsSymbol: unique symbol = Symbol('@@contexts');

const pipe =
	<R>(functions: ((args: any) => any)[]) =>
	<T>(args: T) =>
		functions.reduce((acc, fn) => fn(acc), args) as unknown as R;

export const createApplyMiddlewares =
	<A extends Action, R extends A = A>(middlewares: Middleware[]) =>
	(action: A): R =>
		pipe<R>(middlewares)(action);

const sideEffectToMiddleware =
	<S extends SideEffect<any>>(sideEffect: S): Middleware<Parameters<S>[0]['action']['payload']> =>
	(action) => {
		sideEffect({ action, contexts: extractContextsFromAction(action) ?? {} });

		return action;
	};

export const createActionBus = <Actions extends ActionDeclaration>(
	config?: Config,
): ActionBus<Actions> => {
	type ExpandedAction = ExpandAction<Actions>;

	type BusSubscriber = Subscriber<ExpandedAction>;

	const middlewares = (config?.sideEffects ?? []).map((sideEffect) =>
		sideEffectToMiddleware(sideEffect),
	);

	const applyMiddlewares = createApplyMiddlewares<ExpandedAction>(middlewares);

	const subscribers = new Map<BusSubscriber[0], BusSubscriber[1]>([]);

	const updateSubscriber =
		([selectors, subscription]: [BusSubscriber[1], BusSubscriber[0], ...any]) =>
		(action: ExpandedAction) => {
			if (selectors.includes(action.type)) {
				subscription(action.payload);
			}
		};

	const updateSubscibers = (action: ExpandedAction) => {
		subscribers.forEach((...subscriber) => updateSubscriber(subscriber)(action));
	};

	const dispatchWithContexts =
		(contexts?: Contexts): Dispatch<Actions> =>
		(...args) => {
			const [type, payload] = args;

			const action: Record<string, any> = {
				type,
				payload: payload ?? {},
			};

			if (contexts) {
				// This attaches contexts from the module for analytics
				// It is delibrately excluded from types and
				// made readonly
				Object.defineProperty(action, ContextsSymbol, {
					value: contexts,
					writable: false,
					enumerable: false,
				});
			}

			updateSubscibers(applyMiddlewares(action as ExpandedAction));
		};

	return {
		dispatch: dispatchWithContexts(),
		dispatchWithContexts,
		on: (...[action, callback]: [BusSubscriber[1][0], BusSubscriber[0]]) => {
			subscribers.set(callback, [action]);
			return () => {
				subscribers.delete(callback);
			};
		},
	};
};

export const createActionBusWithContextsFactory = <Actions extends ActionDeclaration>(
	config: Config,
): ((contexts: SurfaceSdkContexts) => CreateActionBusAtModule<Actions>) => {
	const { on, dispatchWithContexts } = createActionBus<Actions>(config);

	return (contexts) =>
		({ data: module, reasons }) => ({
			on,
			dispatch: dispatchWithContexts({
				...contexts,
				module,
				reasons,
			}),
		});
};
